/**
 * Todo Panel Component
 * Displays agent's todo list from Claude SDK TodoWrite tool
 * Sliding panel on right side of chat
 */

import React from 'react';
import { X, CheckCircle2, Circle, Loader2, ChevronRight, ListTodo } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useChatStore } from '@/stores/chatStore';

interface TodoItemProps {
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
  activeForm: string;
}

const TodoItem: React.FC<TodoItemProps> = ({ content, status, activeForm }) => {
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />;
      case 'in_progress':
        return <Loader2 className="h-4 w-4 text-amber-500 animate-spin flex-shrink-0" />;
      default:
        return <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
    }
  };

  const displayText = status === 'in_progress' ? activeForm : content;

  return (
    <div
      className={cn(
        "flex items-start gap-2 p-2 rounded-md transition-colors",
        status === 'completed' && "opacity-60",
        status === 'in_progress' && "bg-amber-500/10 border border-amber-500/20"
      )}
    >
      {getStatusIcon()}
      <span
        className={cn(
          "text-sm",
          status === 'completed' && "line-through text-muted-foreground",
          status === 'in_progress' && "text-amber-600 dark:text-amber-400 font-medium"
        )}
      >
        {displayText}
      </span>
    </div>
  );
};

interface TodoPanelProps {
  className?: string;
}

export const TodoPanel: React.FC<TodoPanelProps> = ({ className }) => {
  const {
    agentTodos,
    todoStats,
    isTodoPanelOpen,
    toggleTodoPanel,
    setTodoPanelOpen,
    executionState
  } = useChatStore();

  // Don't render if no todos
  if (agentTodos.length === 0 && !isTodoPanelOpen) {
    return null;
  }

  return (
    <>
      {/* Toggle button when panel is closed */}
      {!isTodoPanelOpen && agentTodos.length > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={toggleTodoPanel}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-40 rounded-l-lg rounded-r-none border-r-0 px-2 py-6 shadow-md"
          title="Show agent tasks"
        >
          <div className="flex flex-col items-center gap-1">
            <ListTodo className="h-4 w-4" />
            <span className="text-xs">{todoStats.completed}/{todoStats.total}</span>
            <ChevronRight className="h-3 w-3 rotate-180" />
          </div>
        </Button>
      )}

      {/* Sliding panel */}
      <div
        className={cn(
          "fixed right-0 top-0 h-full bg-background border-l shadow-lg z-50",
          "transition-transform duration-300 ease-in-out",
          "w-80 flex flex-col",
          isTodoPanelOpen ? "translate-x-0" : "translate-x-full",
          className
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <ListTodo className="h-5 w-5" />
            <h3 className="font-semibold">Agent Tasks</h3>
            {executionState === 'running' && (
              <span className="text-xs bg-amber-500/20 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-full">
                Running
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTodoPanelOpen(false)}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Progress section */}
        <div className="p-4 border-b bg-muted/30">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Progress</span>
            <span className="text-sm font-medium">
              {todoStats.completed}/{todoStats.total} ({todoStats.percentComplete}%)
            </span>
          </div>
          <Progress value={todoStats.percentComplete} className="h-2" />
          <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
            <span>{todoStats.pending} pending</span>
            <span>{todoStats.inProgress} in progress</span>
            <span>{todoStats.completed} done</span>
          </div>
        </div>

        {/* Todo list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-1">
          {agentTodos.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <ListTodo className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No tasks yet</p>
              <p className="text-xs mt-1">Tasks will appear here when the agent starts working</p>
            </div>
          ) : (
            <>
              {/* In progress items first */}
              {agentTodos.filter(t => t.status === 'in_progress').map((todo, idx) => (
                <TodoItem
                  key={`in-progress-${idx}`}
                  content={todo.content}
                  status={todo.status}
                  activeForm={todo.activeForm}
                />
              ))}
              {/* Pending items */}
              {agentTodos.filter(t => t.status === 'pending').map((todo, idx) => (
                <TodoItem
                  key={`pending-${idx}`}
                  content={todo.content}
                  status={todo.status}
                  activeForm={todo.activeForm}
                />
              ))}
              {/* Completed items */}
              {agentTodos.filter(t => t.status === 'completed').map((todo, idx) => (
                <TodoItem
                  key={`completed-${idx}`}
                  content={todo.content}
                  status={todo.status}
                  activeForm={todo.activeForm}
                />
              ))}
            </>
          )}
        </div>

        {/* Footer with close hint */}
        <div className="p-3 border-t text-center">
          <span className="text-xs text-muted-foreground">
            Click × or outside to close
          </span>
        </div>
      </div>

      {/* Backdrop overlay when panel is open */}
      {isTodoPanelOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40"
          onClick={() => setTodoPanelOpen(false)}
        />
      )}
    </>
  );
};
